import React from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { FieldGroup } from '@xsolla/ui-core';

import { OAuthConnectionFieldsType } from '../../../api/configurations/oauth2connection/types';
import { FieldInput, FieldPassword, MultiInput } from '../../ui';
import { EnterpriseFieldWrapper } from './style';
import { CLIENT_ID_MAX_LENGTH, CLIENT_NAME_MAX_LENGTH, CLIENT_NAME_MIN_LENGTH } from './validator';

export const EnterpriseFields = (): JSX.Element => {
  const intl = useIntl();
  const {
    mutators: { addScope, deleteScope }
  } = useForm<OAuthConnectionFieldsType>();
  const {
    values: { scope },
    errors
  } = useFormState<OAuthConnectionFieldsType>();

  return (
    <EnterpriseFieldWrapper>
      <FieldGroup
        label={intl.formatMessage({
          id: 'enterprise-connection.settings.integration-name.label'
        })}
        tooltip={intl.formatMessage({
          id: 'enterprise-connection.settings.integration-name.tooltip'
        })}>
        <FieldInput
          name="name_prefix"
          data-test-id="oauth2_field_name_prefix"
          placeholder={intl.formatMessage({
            id: 'enterprise-connection.settings.integration-name.placeholder'
          })}
          errorMessage={
            errors &&
            errors.name_prefix &&
            intl.formatMessage(
              { id: errors.name_prefix },
              {
                number: CLIENT_NAME_MAX_LENGTH,
                minNumber: CLIENT_NAME_MIN_LENGTH
              }
            )
          }
        />
      </FieldGroup>

      <FieldGroup
        label={intl.formatMessage({
          id: 'enterprise-connection.settings.authorization-endpoint.label'
        })}
        tooltip={intl.formatMessage({
          id: 'enterprise-connection.settings.authorization-endpoint.tooltip'
        })}>
        <FieldInput
          name="url_authorize"
          placeholder={intl.formatMessage({
            id: 'enterprise-connection.settings.authorization-endpoint.placeholder'
          })}
          data-test-id="oauth2_field_url_authorize"
          errorMessage={
            errors && errors.url_authorize && intl.formatMessage({ id: errors.url_authorize })
          }
        />
      </FieldGroup>

      <FieldGroup
        label={intl.formatMessage({ id: 'enterprise-connection.settings.token-endpoint.label' })}
        tooltip={intl.formatMessage({
          id: 'enterprise-connection.settings.token-endpoint.tooltip'
        })}>
        <FieldInput
          name="url_access_token"
          placeholder={intl.formatMessage({
            id: 'enterprise-connection.settings.token-endpoint.placeholder'
          })}
          data-test-id="oauth2_field_url_access_token"
          errorMessage={
            errors && errors.url_access_token && intl.formatMessage({ id: errors.url_access_token })
          }
        />
      </FieldGroup>

      <FieldGroup
        label={intl.formatMessage({
          id: 'enterprise-connection.settings.url_resource_owner_details.label'
        })}
        tooltip={intl.formatMessage({
          id: 'enterprise-connection.settings.url_resource_owner_details.tooltip'
        })}>
        <FieldInput
          name="url_resource_owner_details"
          placeholder={intl.formatMessage({
            id: 'enterprise-connection.settings.url_resource_owner_details.placeholder'
          })}
          data-test-id="oauth2_field_url_resource_owner_details"
          errorMessage={
            errors &&
            errors.url_resource_owner_details &&
            intl.formatMessage({ id: errors.url_resource_owner_details })
          }
        />
      </FieldGroup>

      <FieldGroup
        label={intl.formatMessage({ id: 'enterprise-connection.settings.client-id.label' })}
        tooltip={intl.formatMessage({ id: 'enterprise-connection.settings.client-id.tooltip' })}>
        <FieldInput
          name="client_id"
          placeholder={intl.formatMessage({
            id: 'enterprise-connection.settings.client-id.placeholder'
          })}
          data-test-id="oauth2_field_client_id"
          errorMessage={
            errors &&
            errors.client_id &&
            intl.formatMessage({ id: errors.client_id }, { number: CLIENT_ID_MAX_LENGTH })
          }
        />
      </FieldGroup>

      <FieldGroup
        label={intl.formatMessage({ id: 'enterprise-connection.settings.client-secret.label' })}
        tooltip={intl.formatMessage({
          id: 'enterprise-connection.settings.client-secret.tooltip'
        })}>
        <FieldPassword
          name="client_secret"
          placeholder={intl.formatMessage({
            id: 'enterprise-connection.settings.client-secret.placeholder'
          })}
          data-test-id="oauth2_field_client_secret"
          errorMessage={
            errors &&
            errors.client_secret &&
            intl.formatMessage({ id: errors.client_secret }, { number: CLIENT_ID_MAX_LENGTH })
          }
        />
      </FieldGroup>

      <MultiInput
        name="scope"
        data-test-id="oauth2_field_scope"
        mainPlaceholder={intl.formatMessage({
          id: 'enterprise-connection.settings.scope.placeholder'
        })}
        extraPlaceholder={intl.formatMessage({
          id: 'enterprise-connection.settings.scope.placeholder'
        })}
        deleteInput={deleteScope}
        addInput={addScope}
        values={scope}
        label={intl.formatMessage({
          id: 'enterprise-connection.settings.scope.label'
        })}
        errorMessage={errors && errors.scope}
      />
    </EnterpriseFieldWrapper>
  );
};
